import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (done || loading) return; // guard against re-submission after success
    if (pw1.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres."); return; }
    if (pw1 !== pw2) { toast.error("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      // Call the endpoint directly (do NOT auto-login via auth context) so the user is redirected to /login as required.
      await api.post("/auth/invitation/accept", { token, password: pw1 });
      setDone(true);
      toast.success("¡Tu cuenta ha sido activada correctamente! Ya puedes iniciar sesión.");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      // If we already finished successfully, ignore any late-arriving error
      if (done) return;
      toast.error(err?.response?.data?.detail || "No se pudo completar el registro.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain flex items-center justify-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl font-light mb-3">Invitación no válida</h1>
          <Link to="/login" className="btn-pink inline-block px-6 py-3 mt-4">Ir al inicio de sesión</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--mep-black)] text-white mep-grain flex flex-col">
      <header className="px-5 md:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-sm text-[var(--mep-text-muted)] hover:text-white"><ArrowLeft size={16} /> Volver</Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">
          <div className="mep-pill-gold mb-4">Invitación premium</div>
          <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-2">Crea tu cuenta</h1>
          <p className="text-[var(--mep-text-muted)] mb-8">Tu pago ya fue recibido. Elige una contraseña para activar tu acceso premium.</p>

          {done ? (
            <div
              data-testid="invite-success-card"
              className="p-6 rounded-xl bg-[rgba(212,175,55,0.10)] border border-[var(--mep-gold)]/40 text-center"
            >
              <CheckCircle2 size={36} className="text-[var(--mep-gold)] mx-auto mb-3" />
              <p className="text-base text-[var(--mep-text)] font-medium">
                ¡Tu cuenta ha sido activada correctamente! Ya puedes iniciar sesión.
              </p>
              <p className="text-xs text-[var(--mep-text-muted)] mt-2">
                Te llevamos al inicio de sesión...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Nueva contraseña</label>
                <input data-testid="invite-pw1" type="password" value={pw1} required minLength={6} onChange={(e)=>setPw1(e.target.value)} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[var(--mep-text-muted)] mb-2 block">Confirmar contraseña</label>
                <input data-testid="invite-pw2" type="password" value={pw2} required minLength={6} onChange={(e)=>setPw2(e.target.value)} className="w-full bg-[var(--mep-surface)] border border-[var(--mep-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--mep-pink)]" />
              </div>
              <button data-testid="invite-submit" type="submit" disabled={loading || done} className="btn-pink w-full py-4 font-semibold">
                {loading ? "Activando acceso..." : "Activar mi acceso"}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
